import { useEffect, useState } from "react";
import { useToast } from "../../context/ToastContext";
import { makeRequest } from "../../makeRequest";
import { useCreatePayLink } from "../usePayments";
import { MY_ENDPOINTS } from "@raashidh/shared";

interface BmlPaymentButtonProps {
  entityType: string;     // "order" | "ask" | "donation" | etc
  entityId: string | number;
  amount: number;
  order_id?: number;
  currency?: string;
  className?: string;
  provider: "bml" | "stripe" | "manual",
  redirect_base_url?: string,
  user_id?: number | null,
  meta?: object
}

export default function BmlPaymentButton({
  entityType = "order",
  entityId,
  order_id,
  amount,
  currency = "MVR",
  className = "",
  provider = "bml",
  redirect_base_url,
  user_id,
  meta,
}: BmlPaymentButtonProps) {
  const toast = useToast();
  const [payUrl, setPayUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { mutateAsync: createPayLink, isPending } = useCreatePayLink();

  /* ---------------------------------------------------------
     1️⃣ Check existing payment link (GLOBAL)
  ---------------------------------------------------------- */
  const checkExistingPayment = async () => {
    setLoading(true);
    try {
      const res = await makeRequest(
        `${MY_ENDPOINTS.PAYMENT_APIS.HAS_PAYMENT_LINK.path}?entity_type=${entityType}&entity_id=${entityId}&order_id=${order_id}`,
        { method: "GET" }
      );

      if (res?.exists && res?.payment?.payment_url) {
        setPayUrl(res.payment.payment_url);
      }
    } catch {
      // silent — link might not exist
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkExistingPayment();
  }, [entityType, entityId]);

  /* ---------------------------------------------------------
     2️⃣ Create or reuse payment link
  ---------------------------------------------------------- */
  const handleCreateLink = async () => {
    try {
      const res = await createPayLink({
        order_id: order_id,
        entity_type: entityType,
        entity_id: String(entityId),
        amount,
        currency,
        provider: "bml"
      });

      if (res?.payment_url) {
        setPayUrl(res.payment_url);
        toast.success("Payment link ready");
      } else {
        toast.error("Failed to create payment link");
      }
    } catch (err: any) {
      toast.error(err.message || "Payment error");
    }
  };

  const handleOpenPayment = () => {
    if (!payUrl) {
      toast.error("Payment link not available");
      return;
    }
    window.open(payUrl, "_blank", "noopener,noreferrer");
  };

  const isBusy = loading || isPending;

  /* ---------------------------------------------------------
     3️⃣ UI
  ---------------------------------------------------------- */
  return (
    <div className="w-full">
      {isBusy ? (
        <button
          disabled
          className={`w-full bg-gray-400 text-white py-2 rounded-xl ${className}`}
        >
          Processing...
        </button>
      ) : payUrl ? (
        <button
          onClick={handleOpenPayment}
          className={`w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-xl font-medium ${className}`}
        >
          🏦 Pay via BML
        </button>
      ) : (
        <button
          onClick={handleCreateLink}
          className={`w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-xl font-medium ${className}`}
        >
          💳 Generate BML Pay Link
        </button>
      )}
    </div>
  );
}